import React, { useEffect, useState, useRef } from "react";
import { Link, NavLink } from "react-router-dom";
import "./HeroSection.css";
import { ENDPOINTS } from "../api/api";
import { useTranslation } from "react-i18next";
import "../i18n";

/* ── Typewriter hook (unchanged) ── */
const useTypewriter = (words, speed = 80, pause = 2000) => {
  const [text, setText] = useState("");
  const [wordIdx, setWordIdx] = useState(0);
  const [deleting, setDeleting] = useState(false);
  useEffect(() => {
    const current = words[wordIdx % words.length];
    const timeout = setTimeout(() => {
      if (!deleting) {
        setText(current.slice(0, text.length + 1));
        if (text.length + 1 === current.length)
          setTimeout(() => setDeleting(true), pause);
      } else {
        setText(current.slice(0, text.length - 1));
        if (text.length - 1 === 0) {
          setDeleting(false);
          setWordIdx((i) => i + 1);
        }
      }
    }, deleting ? speed / 2 : speed);
    return () => clearTimeout(timeout);
  }, [text, deleting, wordIdx, words, speed, pause]);
  return text;
};

/* ── Count-up hook (unchanged) ── */
const useCountUp = (target, duration = 2000, trigger) => {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!trigger) return;
    const numeric = parseInt(String(target).replace(/\D/g, ""), 10);
    if (!numeric) return;
    let start = 0;
    const step = numeric / (duration / 16);
    const timer = setInterval(() => {
      start += step;
      if (start >= numeric) { setCount(numeric); clearInterval(timer); }
      else setCount(Math.floor(start));
    }, 16);
    return () => clearInterval(timer);
  }, [target, duration, trigger]);
  const suffix = String(target).replace(/[0-9]/g, "");
  return `${count}${suffix}`;
};

const StatCard = ({ item, trigger }) => {
  const animated = useCountUp(
    String(item.value) + (item.suffix || ""),
    2000,
    trigger
  );
  return (
    <div className="hs-stat">
      <div className="hs-stat-body">
        <span className="hs-stat-value">{animated}</span>
        <span className="hs-stat-label">{item.label}</span>
      </div>
    </div>
  );
};

/* ── Particle canvas (unchanged) ── */
const ParticleCanvas = () => {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let animId, particles = [];
    const resize = () => { canvas.width = canvas.offsetWidth; canvas.height = canvas.offsetHeight; };
    resize();
    window.addEventListener("resize", resize);
    for (let i = 0; i < 50; i++) {
      particles.push({
        x: Math.random() * canvas.width, y: Math.random() * canvas.height,
        r: Math.random() * 1.2 + 0.2,
        vx: (Math.random() - 0.5) * 0.25, vy: (Math.random() - 0.5) * 0.25,
        alpha: Math.random() * 0.28 + 0.05,
      });
    }
    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach((p) => {
        p.x += p.vx; p.y += p.vy;
        if (p.x < 0) p.x = canvas.width; if (p.x > canvas.width) p.x = 0;
        if (p.y < 0) p.y = canvas.height; if (p.y > canvas.height) p.y = 0;
        ctx.beginPath(); ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(195,185,150,${p.alpha})`; ctx.fill();
      });
      animId = requestAnimationFrame(draw);
    };
    draw();
    return () => { cancelAnimationFrame(animId); window.removeEventListener("resize", resize); };
  }, []);
  return <canvas ref={canvasRef} className="hs-particles" />;
};

/* ── Code block content per tab ── */
const CODE_TABS = ["React", "Python", "PostgreSQL", "API"];

const CODE_LINES = {
  React: [
    { n: 1,  cm: true,  c: "// Solviotech — Production client" },
    { n: 2,  c: <><span className="hs-kw">import</span> <span className="hs-hl">React</span><span className="hs-op">, </span><span className="hs-hl">{"{ useState, useEffect }"}</span></> },
    { n: 3,  c: <><span className="hs-kw">&nbsp; from</span> <span className="hs-str">'react'</span><span className="hs-op">;</span></> },
    { n: 4,  c: "" },
    { n: 5,  c: <><span className="hs-kw">const</span> <span className="hs-fn">SolvioApp</span> <span className="hs-op">= () =&gt; {"{"}</span></> },
    { n: 6,  c: <><span className="hs-op">&nbsp; </span><span className="hs-kw">const</span> <span className="hs-op">[</span><span className="hs-hl">data</span><span className="hs-op">, </span><span className="hs-fn">setData</span><span className="hs-op">] = </span><span className="hs-fn">useState</span><span className="hs-op">(null);</span></> },
    { n: 7,  c: "" },
    { n: 8,  c: <><span className="hs-op">&nbsp; </span><span className="hs-fn">useEffect</span><span className="hs-op">{"(() => {"}</span></> },
    { n: 9,  c: <><span className="hs-op">&nbsp;&nbsp;&nbsp; </span><span className="hs-fn">fetch</span><span className="hs-op">(</span><span className="hs-str">'/api/projects'</span><span className="hs-op">)</span></> },
    { n: 10, c: <><span className="hs-op">&nbsp;&nbsp;&nbsp; .</span><span className="hs-fn">then</span><span className="hs-op">(r =&gt; r.</span><span className="hs-fn">json</span><span className="hs-op">()).</span><span className="hs-fn">then</span><span className="hs-op">(</span><span className="hs-fn">setData</span><span className="hs-op">);</span></> },
    { n: 11, c: <><span className="hs-op">&nbsp; {"}, []);"}</span></> },
    { n: 12, c: "" },
    { n: 13, c: <><span className="hs-op">&nbsp; return</span> <span className="hs-op">&lt;</span><span className="hs-fn">Dashboard</span> <span className="hs-kw">data</span><span className="hs-op">{"={data} />;"}</span></> },
    { n: 14, c: <><span className="hs-op">{"};"}</span></> },
    { n: 15, c: "" },
    { n: 16, c: <><span className="hs-kw">export default</span> <span className="hs-fn">SolvioApp</span><span className="hs-op">;</span></> },
  ],
  Python: [
    { n: 1,  cm: true, c: "# Solviotech — Flask API" },
    { n: 2,  c: <><span className="hs-kw">from</span> <span className="hs-hl">flask</span> <span className="hs-kw">import</span> <span className="hs-fn">Flask</span><span className="hs-op">, </span><span className="hs-fn">jsonify</span></> },
    { n: 3,  c: <><span className="hs-kw">from</span> <span className="hs-hl">flask_jwt_extended</span> <span className="hs-kw">import</span> <span className="hs-fn">jwt_required</span></> },
    { n: 4,  c: "" },
    { n: 5,  c: <><span className="hs-hl">app</span> <span className="hs-op">= </span><span className="hs-fn">Flask</span><span className="hs-op">(</span><span className="hs-hl">__name__</span><span className="hs-op">)</span></> },
    { n: 6,  c: "" },
    { n: 7,  c: <><span className="hs-op">@</span><span className="hs-hl">app</span><span className="hs-op">.</span><span className="hs-fn">route</span><span className="hs-op">(</span><span className="hs-str">'/api/projects'</span><span className="hs-op">)</span></> },
    { n: 8,  c: <><span className="hs-op">@</span><span className="hs-fn">jwt_required</span><span className="hs-op">()</span></> },
    { n: 9,  c: <><span className="hs-kw">def</span> <span className="hs-fn">get_projects</span><span className="hs-op">():</span></> },
    { n: 10, c: <><span className="hs-op">&nbsp;&nbsp; </span><span className="hs-hl">projects</span> <span className="hs-op">= </span><span className="hs-fn">Project</span><span className="hs-op">.</span><span className="hs-fn">query</span><span className="hs-op">.</span><span className="hs-fn">all</span><span className="hs-op">()</span></> },
    { n: 11, c: <><span className="hs-op">&nbsp;&nbsp; </span><span className="hs-kw">return</span> <span className="hs-fn">jsonify</span><span className="hs-op">([</span><span className="hs-hl">p</span><span className="hs-op">.</span><span className="hs-fn">to_dict</span><span className="hs-op">() </span><span className="hs-kw">for</span> <span className="hs-hl">p</span> <span className="hs-kw">in</span> <span className="hs-hl">projects</span><span className="hs-op">])</span></> },
  ],
  PostgreSQL: [
    { n: 1,  cm: true, c: "-- Solviotech database schema" },
    { n: 2,  c: <><span className="hs-kw">CREATE TABLE</span> <span className="hs-fn">projects</span> <span className="hs-op">{"("}</span></> },
    { n: 3,  c: <><span className="hs-op">&nbsp;&nbsp; </span><span className="hs-hl">id</span>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; <span className="hs-kw">SERIAL PRIMARY KEY</span><span className="hs-op">,</span></> },
    { n: 4,  c: <><span className="hs-op">&nbsp;&nbsp; </span><span className="hs-hl">title</span>&nbsp;&nbsp;&nbsp; <span className="hs-kw">VARCHAR</span><span className="hs-op">(200) </span><span className="hs-kw">NOT NULL</span><span className="hs-op">,</span></> },
    { n: 5,  c: <><span className="hs-op">&nbsp;&nbsp; </span><span className="hs-hl">featured</span> <span className="hs-kw">BOOLEAN</span><span className="hs-op"> DEFAULT </span><span className="hs-str">false</span><span className="hs-op">,</span></> },
    { n: 6,  c: <><span className="hs-op">&nbsp;&nbsp; </span><span className="hs-hl">created</span>&nbsp; <span className="hs-kw">TIMESTAMP</span><span className="hs-op"> DEFAULT </span><span className="hs-fn">NOW</span><span className="hs-op">()</span></> },
    { n: 7,  c: <><span className="hs-op">{");"}</span></> },
    { n: 8,  c: "" },
    { n: 9,  c: <><span className="hs-kw">SELECT</span> <span className="hs-op">* </span><span className="hs-kw">FROM</span> <span className="hs-fn">projects</span></> },
    { n: 10, c: <><span className="hs-kw">&nbsp;&nbsp; WHERE</span> <span className="hs-hl">featured</span> <span className="hs-op">= </span><span className="hs-str">true</span></> },
    { n: 11, c: <><span className="hs-kw">&nbsp;&nbsp; ORDER BY</span> <span className="hs-hl">created</span> <span className="hs-kw">DESC</span><span className="hs-op">;</span></> },
  ],
  API: [
    { n: 1,  cm: true, c: "# Solviotech REST API" },
    { n: 2,  c: "" },
    { n: 3,  c: <><span className="hs-kw">GET</span>&nbsp;&nbsp; <span className="hs-str">/api/projects</span></> },
    { n: 4,  c: <><span className="hs-kw">GET</span>&nbsp;&nbsp; <span className="hs-str">/api/services</span></> },
    { n: 5,  c: <><span className="hs-kw">GET</span>&nbsp;&nbsp; <span className="hs-str">/api/team-members</span></> },
    { n: 6,  c: <><span className="hs-kw">GET</span>&nbsp;&nbsp; <span className="hs-str">/api/testimonials</span></> },
    { n: 7,  c: <><span className="hs-kw">POST</span> <span className="hs-str">/api/contact-messages</span></> },
    { n: 8,  c: <><span className="hs-kw">POST</span> <span className="hs-str">/api/login</span></> },
    { n: 9,  c: <><span className="hs-kw">POST</span> <span className="hs-str">/api/register</span></> },
    { n: 10, c: "" },
    { n: 11, cm: true, c: "# Secured via JWT + reCAPTCHA v3" },
  ],
};

/* ════════════════════════════════════════════
   MAIN COMPONENT
════════════════════════════════════════════ */
const HeroSection = () => {
  const { t, i18n } = useTranslation();

  const [heroData, setHeroData]               = useState({});
  const [stats, setStats]                     = useState([]);
  const [services, setServices]               = useState([]);
  const [featuredProjects, setFeaturedProjects] = useState([]);
  const [processSteps, setProcessSteps]       = useState([]);
  const [whyUs, setWhyUs]                     = useState([]);
  const [statsVisible, setStatsVisible]       = useState(false);
  const [mounted, setMounted]                 = useState(false);
  const [activeTab, setActiveTab]             = useState("React");
  const [activeWhyTab, setActiveWhyTab]       = useState(0);

  const statsRef    = useRef(null);
  const scrollRef   = useRef(null);

  const dynamicWord = useTypewriter(
    [t("hero.word1"), t("hero.word2"), t("hero.word3"), t("hero.word4"), t("hero.word5")],
    90, 2500
  );

  /* fetch all data (unchanged) */
  useEffect(() => {
    setTimeout(() => setMounted(true), 100);
    const fetchAll = async () => {
      const lang = (i18n.language || "en").split("-")[0].toLowerCase();
      try {
        const [heroRes, statsRes, servicesRes, projectsRes, stepsRes, whyRes] =
          await Promise.all([
            fetch(ENDPOINTS.hero),
            fetch(`${ENDPOINTS.stats}?lang=${lang}`),
            fetch(`${ENDPOINTS.services}?lang=${lang}`),
            fetch(ENDPOINTS.featuredProjects),
            fetch(`${ENDPOINTS.processSteps}?lang=${lang}`),
            fetch(`${ENDPOINTS.whyUs}?lang=${lang}`),
          ]);
        setHeroData(await heroRes.json());
        setStats(await statsRes.json());
        setServices(await servicesRes.json());
        const all = await projectsRes.json();
        setFeaturedProjects(all.filter((p) => p.featured === true));
        setProcessSteps(await stepsRes.json());
        setWhyUs(await whyRes.json());
      } catch (err) { console.error("[HeroSection] Failed to load:", err); }
    };
    fetchAll();
  }, [i18n.language]);

  /* stats intersection observer (unchanged) */
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setStatsVisible(true); },
      { threshold: 0.3 }
    );
    if (statsRef.current) observer.observe(statsRef.current);
    return () => observer.disconnect();
  }, [stats]);

  /* drag-to-scroll on project strip */
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    let isDown = false, startX, scrollLeft;
    const onDown  = (e) => { isDown = true; startX = e.pageX - el.offsetLeft; scrollLeft = el.scrollLeft; };
    const onUp    = ()  => { isDown = false; };
    const onMove  = (e) => { if (!isDown) return; e.preventDefault(); const x = e.pageX - el.offsetLeft; el.scrollLeft = scrollLeft - (x - startX) * 1.5; };
    el.addEventListener("mousedown",  onDown);
    el.addEventListener("mouseleave", onUp);
    el.addEventListener("mouseup",    onUp);
    el.addEventListener("mousemove",  onMove);
    return () => { el.removeEventListener("mousedown",onDown); el.removeEventListener("mouseleave",onUp); el.removeEventListener("mouseup",onUp); el.removeEventListener("mousemove",onMove); };
  }, []);

  const SERVICE_ICONS = [
    "fa-solid fa-code", "fa-solid fa-mobile-screen", "fa-solid fa-brain",
    "fa-solid fa-cloud", "fa-solid fa-palette", "fa-solid fa-server",
  ];

  const lines = CODE_LINES[activeTab] || CODE_LINES.React;

  return (
    <div className={`hs-page ${mounted ? "hs-page--in" : ""}`}>

      {/* ══════════════════════════════
          HERO — two column
      ══════════════════════════════ */}
      <section className="hs-hero">
        <ParticleCanvas />
        <div className="hs-hero-bg">
          <div className="hs-orb hs-orb--1" />
          <div className="hs-orb hs-orb--2" />
          <div className="hs-orb hs-orb--3" />
          <div className="hs-grid" />
          <div className="hs-scanline" />
        </div>

        <div className="hs-hero-inner">

          {/* Left */}
          <div className="hs-hero-left">
            <NavLink to="/portfolio" className="hs-eyebrow">
              <span className="hs-eyebrow-bar"></span>
              {heroData.eyebrow || t("hero.eyebrow") || "View our latest work"}
              <span>&nbsp;→</span>
            </NavLink>

            <h1 className="hs-headline">
              <span className="hs-headline-line">{t("hero.heading1")}</span>
              <span className="hs-headline-line">
                <span className="hs-headline-shimmer">
                  {dynamicWord}<span className="hs-cursor">|</span>
                </span>
              </span>
              <span className="hs-headline-line hs-headline-muted">
                {t("hero.heading3")}
              </span>
            </h1>

            <p className="hs-sub">
              {t("hero.sub").split("Solviotech").map((part, i, arr) =>
                i < arr.length - 1
                  ? <React.Fragment key={i}>{part}<strong className="hs-brand">Solviotech</strong></React.Fragment>
                  : part
              )}
            </p>

            <div className="hs-chips">
              <div className="hs-chip"><i className="fa-brands fa-react"></i>React</div>
              <div className="hs-chip"><i className="fa-brands fa-python"></i>Python</div>
              <div className="hs-chip"><i className="fa-solid fa-database"></i>PostgreSQL</div>
              <div className="hs-chip"><i className="fa-solid fa-cloud"></i>Cloud</div>
              <div className="hs-chip"><i className="fa-solid fa-server"></i>Flask</div>
            </div>

            <div className="hs-actions">
              <Link to="/portfolio" className="hs-cta-primary">
                <span className="hs-cta-glow"></span>
                {heroData.primaryBtn || t("hero.cta1")}
                <i className="fa-solid fa-arrow-right hs-cta-arrow"></i>
              </Link>
              <Link to="/contact" className="hs-cta-secondary">
                {heroData.secondaryBtn || t("hero.cta2")}
                <i className="fa-solid fa-arrow-right"></i>
              </Link>
            </div>
          </div>

          {/* Right — code block */}
          <div className="hs-hero-right">
            <div className="hs-code-card">
              <div className="hs-code-tabs">
                {CODE_TABS.map((tab) => (
                  <div
                    key={tab}
                    className={`hs-code-tab${activeTab === tab ? " active" : ""}`}
                    onClick={() => setActiveTab(tab)}
                  >
                    {tab}
                  </div>
                ))}
              </div>
              <div className="hs-code-body">
                {lines.map((line) => (
                  <div className="hs-code-line" key={line.n}>
                    <span className="hs-line-num">{line.n}</span>
                    {line.cm
                      ? <span className="hs-cm">{line.c}</span>
                      : <span>{line.c}</span>
                    }
                  </div>
                ))}
              </div>
              <div className="hs-code-footer">
                <span className="hs-code-footer-link">Documentation →</span>
                <span className="hs-code-version">v2.4.1</span>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* ══════════════════════════════
          TRUSTED STRIP
      ══════════════════════════════ */}
      <div className="hs-trusted-strip">
        <div className="hs-trusted-label">Trusted by businesses across Kenya &amp; beyond</div>
        <div className="hs-trusted-logos">
          <div className="hs-logo-item"><span className="hs-logo-dot"></span>StartupKE</div>
          <div className="hs-logo-item"><span className="hs-logo-dot"></span>TechAfrica</div>
          <div className="hs-logo-item"><span className="hs-logo-dot"></span>FinBridge</div>
          <div className="hs-logo-item"><span className="hs-logo-dot"></span>DataCore</div>
          <div className="hs-logo-item"><span className="hs-logo-dot"></span>CloudNest</div>
          <div className="hs-logo-item"><span className="hs-logo-dot"></span>NexaHub</div>
          <div className="hs-logo-item"><span className="hs-logo-dot"></span>BuildKE</div>
          <div className="hs-logo-item"><span className="hs-logo-dot"></span>AfriSaaS</div>
          <div className="hs-logo-item"><span className="hs-logo-dot"></span>StartupKE</div>
          <div className="hs-logo-item"><span className="hs-logo-dot"></span>TechAfrica</div>
          <div className="hs-logo-item"><span className="hs-logo-dot"></span>FinBridge</div>
          <div className="hs-logo-item"><span className="hs-logo-dot"></span>DataCore</div>
          <div className="hs-logo-item"><span className="hs-logo-dot"></span>CloudNest</div>
          <div className="hs-logo-item"><span className="hs-logo-dot"></span>NexaHub</div>
          <div className="hs-logo-item"><span className="hs-logo-dot"></span>BuildKE</div>
          <div className="hs-logo-item"><span className="hs-logo-dot"></span>AfriSaaS</div>
        </div>
      </div>

      {/* ══════════════════════════════
          STATS
      ══════════════════════════════ */}
      <section className="hs-stats-section" ref={statsRef}>
        <div className="hs-stats-inner">
          {stats.map((item, i) => (
            <StatCard key={item.id || i} item={item} trigger={statsVisible} />
          ))}
        </div>
      </section>

      {/* ══════════════════════════════
          SERVICES
      ══════════════════════════════ */}
      <section className="hs-services-section">
        <div className="hs-container">
          <div className="hs-section-header">
            <span className="hs-label">{t("services.eyebrow")}</span>
            <h2 className="hs-section-title">{t("services.title")}</h2>
            <p className="hs-section-desc">{t("services.desc")}</p>
          </div>
          <div className="hs-services-grid">
            {services.map((service, i) => (
              <div className="hs-service-card" key={service.id || i}>
                <div className="hs-service-visual">
                  {i % 6 === 0 && (
                    <div className="svc-browser">
                      <div className="svc-browser-bar">
                        <span className="svc-dot svc-dot-r"></span>
                        <span className="svc-dot svc-dot-y"></span>
                        <span className="svc-dot svc-dot-g"></span>
                        <span className="svc-browser-url"></span>
                      </div>
                      <div className="svc-code-lines">
                        <div className="svc-code-line gold"></div>
                        <div className="svc-code-line long"></div>
                        <div className="svc-code-line green"></div>
                        <div className="svc-code-line med"></div>
                        <div className="svc-code-line short"></div>
                        <div className="svc-code-line long"></div>
                      </div>
                    </div>
                  )}
                  {i % 6 === 1 && (
                    <div style={{display:"flex",gap:"16px",alignItems:"center"}}>
                      <div className="svc-phone">
                        <div className="svc-phone-notch"></div>
                        <div className="svc-phone-screen">
                          <div className="svc-phone-row gold"></div>
                          <div className="svc-phone-row"></div>
                          <div className="svc-phone-row short"></div>
                          <div className="svc-phone-btn"></div>
                          <div className="svc-phone-row" style={{marginTop:"4px"}}></div>
                          <div className="svc-phone-row short"></div>
                        </div>
                        <div className="svc-phone-home"></div>
                      </div>
                      <div className="svc-phone" style={{transform:"translateY(10px)",opacity:0.5}}>
                        <div className="svc-phone-notch"></div>
                        <div className="svc-phone-screen">
                          <div className="svc-phone-row gold"></div>
                          <div className="svc-phone-row"></div>
                          <div className="svc-phone-row short"></div>
                        </div>
                        <div className="svc-phone-home"></div>
                      </div>
                    </div>
                  )}
                  {i % 6 === 2 && (
                    <div className="svc-radar">
                      <div className="svc-radar-ring"></div>
                      <div className="svc-radar-ring"></div>
                      <div className="svc-radar-ring"></div>
                      <div className="svc-radar-sweep"></div>
                      <div className="svc-radar-dot svc-radar-dot-1"></div>
                      <div className="svc-radar-dot svc-radar-dot-2"></div>
                      <div className="svc-radar-dot svc-radar-dot-3"></div>
                    </div>
                  )}
                  {i % 6 === 3 && (
                    <div className="svc-terminal">
                      <div className="svc-terminal-bar">
                        <span className="svc-dot svc-dot-r"></span>
                        <span className="svc-dot svc-dot-y"></span>
                        <span className="svc-dot svc-dot-g"></span>
                        <span className="svc-terminal-title">bash</span>
                      </div>
                      <div className="svc-terminal-body">
                        <div className="svc-terminal-line cmd">$ npm run deploy</div>
                        <div className="svc-terminal-line">▸ Building production bundle...</div>
                        <div className="svc-terminal-line">▸ Pushing to VPS 203.161.38.x</div>
                        <div className="svc-terminal-line dim">▸ Restarting gunicorn service</div>
                        <div className="svc-terminal-line">✓ Deployed successfully <span className="svc-terminal-cursor"></span></div>
                      </div>
                    </div>
                  )}
                  {i % 6 === 4 && (
                    <div className="svc-design">
                      <div className="svc-wireframe">
                        <div className="svc-wf-header"></div>
                        <div className="svc-wf-row w80"></div>
                        <div className="svc-wf-row w60"></div>
                        <div className="svc-wf-row w80"></div>
                        <div className="svc-wf-row w60"></div>
                        <div className="svc-wf-btn"></div>
                      </div>
                      <div className="svc-palette">
                        <div className="svc-swatch" style={{background:"#020f09",border:"1px solid rgba(195,185,150,0.3)"}}></div>
                        <div className="svc-swatch" style={{background:"#c3b996"}}></div>
                        <div className="svc-swatch" style={{background:"#0a6b5a"}}></div>
                        <div className="svc-swatch" style={{background:"rgba(255,255,255,0.08)",border:"1px solid rgba(255,255,255,0.1)"}}></div>
                      </div>
                    </div>
                  )}
                  {i % 6 === 5 && (
                    <div className="svc-flow">
                      <div className="svc-flow-node active">Discovery</div>
                      <div className="svc-flow-arrow">↓</div>
                      <div className="svc-flow-node active">Architecture</div>
                      <div className="svc-flow-arrow">↓</div>
                      <div className="svc-flow-node">Implementation</div>
                      <div className="svc-flow-arrow">↓</div>
                      <div className="svc-flow-node">Delivery</div>
                    </div>
                  )}
                </div>
                <div className="hs-service-body">
                  <div className="hs-service-icon">
                    <i className={service.icon || SERVICE_ICONS[i % 6]}></i>
                  </div>
                  <h3 className="hs-service-name">{service.name}</h3>
                  <p className="hs-service-desc">{service.description}</p>
                  <NavLink to="/services" className="hs-service-link">
                    {t("services.learnMore")} <i className="fa-solid fa-arrow-right"></i>
                  </NavLink>
                </div>
              </div>
            ))}
          </div>
          <div className="hs-center">
            <NavLink to="/services" className="hs-outline-btn">
              <i className="fa-solid fa-layer-group"></i>
              {t("services.exploreAll")}
            </NavLink>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════
          PROCESS
      ══════════════════════════════ */}
      <section className="hs-process-section">
        <div className="hs-container">
          <div className="hs-section-header">
            <span className="hs-label">{t("process.eyebrow")}</span>
            <h2 className="hs-section-title">{t("process.title")}</h2>
            <p className="hs-section-desc">{t("process.desc")}</p>
          </div>
          <div className="hs-process-grid">
            {processSteps.map((step, i) => (
              <div className="hs-process-card" key={step.id || i}>

                {/* ── Visual pictorial per step ── */}
                <div className="hs-process-visual">

                  {/* 01 Discover — checklist */}
                  {i % 5 === 0 && (
                    <div className="proc-discover">
                      {[
                        { done: true,  gold: true,  label: "Goals defined" },
                        { done: true,  gold: false, label: "Users mapped" },
                        { done: false, gold: false, label: "Tech scoped" },
                        { done: false, gold: false, label: "Timeline set" },
                      ].map((row, idx) => (
                        <div className="proc-discover-row" key={idx}>
                          <div className={`proc-discover-check${row.done ? " done" : ""}`}></div>
                          <div className={`proc-discover-line${row.gold ? " gold" : ""}`}></div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* 02 Design — wireframe */}
                  {i % 5 === 1 && (
                    <div className="proc-design">
                      <div className="proc-design-topbar">
                        <div className="proc-design-tab active"></div>
                        <div className="proc-design-tab"></div>
                        <div className="proc-design-tab"></div>
                      </div>
                      <div className="proc-design-box">
                        <div className="proc-design-box-inner"></div>
                      </div>
                      <div className="proc-design-row w70"></div>
                      <div className="proc-design-row w50"></div>
                      <div className="proc-design-row w70"></div>
                      <div className="proc-design-btn"></div>
                    </div>
                  )}

                  {/* 03 Build — code editor */}
                  {i % 5 === 2 && (
                    <div className="proc-build">
                      <div className="proc-build-bar">
                        <span className="svc-dot svc-dot-r"></span>
                        <span className="svc-dot svc-dot-y"></span>
                        <span className="svc-dot svc-dot-g"></span>
                      </div>
                      <div className="proc-build-body">
                        {[
                          [{ c:"kw" },{ c:"fn" },{ c:"long" }],
                          [{ c:"str" },{ c:"med" }],
                          [{ c:"kw" },{ c:"long" }],
                          [{ c:"fn" },{ c:"str" },{ c:"med" }],
                          [{ c:"long" }],
                          [{ c:"kw" },{ c:"fn" }],
                        ].map((row, ri) => (
                          <div className="proc-build-line" key={ri}>
                            <span className="proc-build-num">{ri + 1}</span>
                            {row.map((seg, si) => (
                              <div key={si} className={`proc-build-code ${seg.c}`}></div>
                            ))}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* 04 QA — test results */}
                  {i % 5 === 3 && (
                    <div className="proc-qa">
                      <div className="proc-qa-row pass">
                        <span className="proc-qa-icon g">✓</span>
                        <span className="proc-qa-label">auth.test.js</span>
                        <span className="proc-qa-badge pass">PASS</span>
                      </div>
                      <div className="proc-qa-row pass">
                        <span className="proc-qa-icon g">✓</span>
                        <span className="proc-qa-label">api.test.js</span>
                        <span className="proc-qa-badge pass">PASS</span>
                      </div>
                      <div className="proc-qa-row fail">
                        <span className="proc-qa-icon r">✕</span>
                        <span className="proc-qa-label">ui.test.js</span>
                        <span className="proc-qa-badge fail">FAIL</span>
                      </div>
                      <div className="proc-qa-row run">
                        <span className="proc-qa-icon y">◌</span>
                        <span className="proc-qa-label">perf.test.js</span>
                        <span className="proc-qa-badge run">RUN</span>
                      </div>
                    </div>
                  )}

                  {/* 05 Launch — rocket */}
                  {i % 5 === 4 && (
                    <div className="proc-launch">
                      <div className="proc-launch-rocket">🚀</div>
                      <div className="proc-launch-trail">
                        <div className="proc-launch-dot"></div>
                        <div className="proc-launch-dot"></div>
                        <div className="proc-launch-dot"></div>
                        <div className="proc-launch-dot"></div>
                      </div>
                      <div className="proc-launch-badge">✓ Live</div>
                    </div>
                  )}
                </div>

                {/* ── Text body ── */}
                <div className="hs-process-body">
                  <div className="hs-process-num">0{i + 1}</div>
                  <h3 className="hs-process-title">{step.title}</h3>
                  <p className="hs-process-desc">{step.desc}</p>
                </div>

              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════
          FEATURED PROJECTS
      ══════════════════════════════ */}
      <section className="hs-projects-section">
        <div className="hs-container">
          <div className="hs-section-header">
            <span className="hs-label">{t("projects.eyebrow")}</span>
            <h2 className="hs-section-title">{t("projects.title")}</h2>
            <p className="hs-section-desc">{t("projects.desc")}</p>
          </div>
          <div className="hs-projects-scroll-wrap">
            <div className="hs-projects-grid" ref={scrollRef}>
              {featuredProjects.map((project, i) => (
                <div className="hs-project-card" key={project.id || i}>
                  <div className="hs-project-img-wrap">
                    {project.image ? (
                      <img
                        src={project.image}
                        alt={project.title}
                        className="hs-project-img"
                        onError={(e) => { e.target.style.display = "none"; }}
                      />
                    ) : (
                      <div className="hs-project-placeholder">
                        <div className="hs-project-placeholder-inner">
                          <div className="hs-project-placeholder-bars">
                            {[40,65,50,80,55,70,45].map((h,idx) => (
                              <div key={idx} className="hs-project-placeholder-bar" style={{height:`${h}px`}}></div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                    <div className="hs-project-overlay">
                      <h3 className="hs-project-title">{project.title}</h3>
                      {project.technologies && project.technologies.length > 0 && (
                        <div className="hs-project-title-sub">
                          {project.technologies.slice(0,3).join(" · ")}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="hs-center">
            <Link to="/portfolio" className="hs-glow-btn">
              <i className="fa-solid fa-briefcase"></i>
              {t("projects.viewAll")}
            </Link>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════
          WHY US
      ══════════════════════════════ */}
      <section className="hs-why-section">
        <div className="hs-container">
          <div className="hs-section-header">
            <span className="hs-label">{t("why.eyebrow")}</span>
            <h2 className="hs-section-title">{t("why.title")}</h2>
          </div>
          {/* Tab buttons */}
          {whyUs.length > 0 && (
            <>
              <div className="hs-why-tabs" style={{marginBottom:"0"}}>
                {whyUs.map((item, i) => (
                  <button
                    key={item.id || i}
                    className={`hs-why-tab${activeWhyTab === i ? " active" : ""}`}
                    onClick={() => setActiveWhyTab(i)}
                  >
                    {item.title}
                  </button>
                ))}
              </div>

              {/* Panel */}
              {whyUs[activeWhyTab] && (() => {
                const item = whyUs[activeWhyTab];
                const SUB_ITEMS = [
                  { icon: "fa-solid fa-shield-halved", title: "Secure by default",    desc: "JWT auth, rate limiting, and reCAPTCHA built in from day one." },
                  { icon: "fa-solid fa-bolt",          title: "Fast delivery",         desc: "Clear sprints, daily updates, no surprises — shipped on time." },
                  { icon: "fa-solid fa-globe",         title: "Africa-first thinking", desc: "Mobile-first, M-Pesa ready, multilingual from the start." },
                ];
                return (
                  <div className="hs-why-panel">
                    <div className="hs-why-arc"></div>
                    <div className="hs-why-arc-2"></div>
                    <div className="hs-why-panel-badge">{item.title}</div>
                    <div className="hs-why-panel-title">{item.title}</div>
                    <div className="hs-why-grid">
                      {SUB_ITEMS.map((sub, si) => (
                        <div className="hs-why-card" key={si}>
                          <div className="hs-why-icon"><i className={sub.icon}></i></div>
                          <h3 className="hs-why-title">{sub.title}</h3>
                          <p className="hs-why-desc">{sub.desc}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}
            </>
          )}
        </div>
      </section>


      {/* ══════════════════════════════
          CTA STRIP
      ══════════════════════════════ */}
      <section className="hs-cta-strip">
        <div className="hs-cta-inner">
          <div className="hs-cta-orb"></div>
          <div className="hs-cta-text">
            <span className="hs-label">
              <i className="fa-solid fa-bolt"></i> {t("cta.eyebrow")}
            </span>
            <h2 className="hs-cta-title">{t("cta.title")}</h2>
            <p className="hs-cta-sub">{t("cta.sub")}</p>
          </div>
          <div className="hs-cta-btns">
            <Link to="/contact" className="hs-cta-primary">
              <span className="hs-cta-glow"></span>
              <i className="fa-solid fa-paper-plane"></i>
              {t("cta.btn1")}
            </Link>
            <Link to="/team" className="hs-outline-btn">
              {t("cta.btn2")}
              <i className="fa-solid fa-arrow-right"></i>
            </Link>
          </div>
        </div>
      </section>

    </div>
  );
};

export default HeroSection;